/**
 * Executes stored auth intents that may need async work before navigation.
 *
 * `resolveAuthIntent()` remains the pure routing contract. This module layers
 * auth-completion side effects on top of that contract so web callback,
 * signup completion, and native post-auth all share the same behavior.
 */
import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  acceptSpaceInvitation,
  getQuestionByID,
  setEventGoingApiV1EventsEventIdGoingPost,
  votePoll,
} from '@api-client/api-client';
import { claimLive, joinLive } from '@api-client/liveRoom';
import { getLiveState } from '@api-client/liveReads';
import type { LiveClaimRead, LiveJoinBody } from '@models/api';
import type { PollVoteResponse } from '@models/api/poll';
import { liveMeKey } from '@lib/cache/polls';
import { LIVE_ERROR_CODES } from '@lib/polls/pollErrors';
import { LIVE_POLL_COPY } from '@components/livePolls/livePollCopy';
import type { SpaceSummary } from '@models/api/question';
import { writePollIntoCaches } from '@lib/cache/polls';
import { invalidateMembershipQueries } from '@lib/cache/spaces';
import { POLL_ERROR_CODES, pollErrorCode, pollErrorSpaceId } from '@lib/polls/pollErrors';
import { POLL_COPY } from '@components/polls/pollCopy';
import { REQUEST_SENT_TOAST } from '@components/community/spaceAccessCopy';
import { getErrorDetails, getErrorStatus } from '@utils/errorDetection';
import { forgetGuestToken } from '@lib/livePolls/guestToken';
import { QuestionAccessErrorCode, parseQuestionAccessError } from '@utils/questionAccessErrors';
import {
  invalidateAcceptedInvitationQueries,
  sweepAfterFailedAccept,
} from '@hooks/useSpaceInvitations';
import { routes } from '@routes';
import {
  AuthIntent,
  leftViaAddEvent,
  resolveAuthIntent,
  type AuthIntentPayload,
} from '@utils/authIntent';
import {
  buildAttendToast,
  buildPinSheetFailedToast,
  isReadableAttendSummary,
  pinRequiredOf,
  requestedSpacesAreComplete,
  requestedSpacesOf,
} from '@utils/attendSummary';
import { buildRegistrationCelebration } from '@utils/registrationCelebration';
import type { IntentDraft } from '@lib/overlay/intents';
import { invalidateGoingEventQueries } from '@utils/invalidateGoingEventQueries';
import type { SpaceMembershipChange } from '@utils/invalidateSpaceMembershipQueries';
import { isEventCancelledError } from '@utils/eventStatus';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { toast, type ToastOptions } from '@hooks/use-toast';

/** Recovery states surfaced to invite UI after post-auth execution. */
export type InviteRecoveryReason = 'email-mismatch' | 'expired';

/** Location state emitted by the post-auth executor for recovery UI. */
export interface PostAuthIntentExecutionState {
  inviteRecovery?: { reason: InviteRecoveryReason };
}

/** Final navigation target returned after auth-intent execution completes. */
export interface PostAuthIntentExecutionResult {
  path: string;
  state?: PostAuthIntentExecutionState;
  /** Overlay intents to post, in order, immediately before `navigate()`. Absent when the intent
   * kind never queues one, or the underlying action (invite accept, event attend) failed. */
  intents?: IntentDraft[];
}

/** Dependencies required to execute a stored auth intent. */
interface ExecutePostAuthIntentArgs {
  intent: AuthIntentPayload;
  redirectPath: string | null;
  queryClient: QueryClient;
  accessToken?: string | null;
}

/** Builds the clean community URL used after invite acceptance succeeds. */
function getAcceptedInvitePath(
  communitySlug: string,
  { events }: { events?: boolean } = {},
): string {
  return routes.community.details(
    { id: null, slug: communitySlug },
    { returnTo: routes.events(), ...(events ? { query: { tab: 'events' } } : {}) },
  );
}

/**
 * Executes a stored auth intent and returns the next navigation target.
 *
 * Most intents are route-only and delegate directly to `resolveAuthIntent()`.
 * Two kinds do async work after auth before navigating:
 * - `GOING_EVENT` finishes the "Going" RSVP (attend + auto-join the event's spaces).
 * - `ACCEPT_SPACE_INVITE` performs the invite accept, invalidates membership caches, and
 *   maps known failures to recovery state for manual invite UI. When the stored redirect path carries
 *   the Add Event marker, a successful (or 409) accept lands on the events tab with a `create-event`
 *   intent instead of the welcome intent.
 *
 * @param args.intent Stored auth intent payload.
 * @param args.redirectPath Stored pre-auth redirect path, if any.
 * @param args.queryClient Query client used for invalidation after mutations.
 * @param args.accessToken Access token for authenticated post-auth API calls.
 */
export async function executePostAuthIntent({
  intent,
  redirectPath,
  queryClient,
  accessToken,
}: ExecutePostAuthIntentArgs): Promise<PostAuthIntentExecutionResult> {
  if (intent.kind === AuthIntent.GOING_EVENT) {
    return executeGoingEvent({ intent, redirectPath, queryClient, accessToken });
  }

  if (intent.kind === AuthIntent.VOTE_POLL) {
    return executeVotePoll({ intent, redirectPath, queryClient, accessToken });
  }

  if (intent.kind === AuthIntent.LIVE_POLL_ANSWERS) {
    return executeLivePollAnswers({ intent, redirectPath, queryClient, accessToken });
  }

  if (intent.kind !== AuthIntent.ACCEPT_SPACE_INVITE) {
    return resolveAuthIntent(intent, redirectPath);
  }

  const fallbackTarget = resolveAuthIntent(intent, redirectPath);

  if (!accessToken) {
    logger.error('[executePostAuthIntent] Missing access token for invite acceptance fallback');
    return fallbackTarget;
  }

  // A signed-out invitee who tapped Add Event is accepted here, before landing, so the add-event drawer
  // (the Space screen consumes `create-event`) follows instead of the "You are in!" drawer.
  const fromAddEvent = leftViaAddEvent(redirectPath, intent.communitySlug);
  const addEventTarget: PostAuthIntentExecutionResult = {
    path: getAcceptedInvitePath(intent.communitySlug, { events: true }),
    intents: [{ surface: 'create-event', payload: { community: intent.communitySlug } }],
  };

  try {
    const { data: accepted } = await acceptSpaceInvitation(
      intent.communitySlug,
      { token: intent.invitationToken },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );

    invalidateAcceptedInvitationQueries(queryClient, intent.communitySlug, intent.invitationToken, {
      kind: 'these',
      ids: [accepted.space_id],
    });

    if (fromAddEvent) return addEventTarget;

    return {
      path: getAcceptedInvitePath(intent.communitySlug),
      intents: [{ surface: 'community-welcome', payload: { community: intent.communitySlug } }],
    };
  } catch (error) {
    // Handle both AxiosError (production) and plain error objects (tests, non-Axios callers)
    const fallbackError = error as { response?: { status?: number } };
    const status = isAxiosError(error) ? error.response?.status : fallbackError.response?.status;

    if (status === 403) {
      return {
        ...fallbackTarget,
        state: { inviteRecovery: { reason: 'email-mismatch' } },
      };
    }

    if (status === 410) {
      return {
        ...fallbackTarget,
        state: { inviteRecovery: { reason: 'expired' } },
      };
    }

    sweepAfterFailedAccept(queryClient, intent.communitySlug, intent.invitationToken, status);

    if (status === 409) {
      return fromAddEvent ? addEventTarget : { path: getAcceptedInvitePath(intent.communitySlug) };
    }

    logger.error('[executePostAuthIntent] Failed to execute auth intent:', sanitizeError(error));
    return fallbackTarget;
  }
}

/**
 * Finishes a "Going" RSVP after auth: POSTs the attend (RSVP + auto-join the event's spaces),
 * shows the joined-spaces summary toast, refreshes the affected caches, and lands the user back on
 * the event page (resolveAuthIntent returns the stored redirect, else routes.event.details).
 *
 * Best-effort by design: a failed attend (missing token, 404, network) is logged and the user
 * is still routed to the fallback target, never blocked or shown an alarming error on their
 * very first authenticated screen. They can re-tap "Going" in-app to retry. A rejected POST
 * still sweeps the caches (it may have committed); a missing token sends nothing and sweeps nothing.
 */
async function executeGoingEvent({
  intent,
  redirectPath,
  queryClient,
  accessToken,
}: {
  intent: Extract<AuthIntentPayload, { kind: typeof AuthIntent.GOING_EVENT }>;
  redirectPath: string | null;
  queryClient: QueryClient;
  accessToken?: string | null;
}): Promise<PostAuthIntentExecutionResult> {
  const fallbackTarget = resolveAuthIntent(intent, redirectPath);

  if (!accessToken) {
    logger.error('[executePostAuthIntent] Missing access token for going-event attend');
    return fallbackTarget;
  }

  try {
    const { data } = await setEventGoingApiV1EventsEventIdGoingPost(intent.eventId, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    // ONE readability decision for the whole block, mirroring the share pipeline's G6 guard. Every
    // line below indexes into the four buckets, and `buildAttendToast` measures three of them, so a
    // 201 with a truthy but malformed body would throw here — into the catch at the bottom. An
    // unreadable body is not evidence the attend did nothing, so it takes the honest branch
    // instead: no toast (there is no summary to report), and a membership outcome of `unknown`.
    const readable = isReadableAttendSummary(data);
    // TECH-663: a host Space with a PIN. The PIN sheet confirms the Going itself, so no toast for
    // joined or pending; a Space that failed is on no card, so it still gets its warning.
    const pins = readable ? pinRequiredOf(data) : [];
    if (readable) {
      const attendToast =
        pins.length === 0 ? buildAttendToast(data) : buildPinSheetFailedToast(data);
      if (attendToast) toast(attendToast);
    } else {
      logger.warn('[executePostAuthIntent] Attend response was unreadable; sweeping wide');
    }
    // EVERY post-auth attend celebrates (design §4.2) — the once-ever guard in
    // CelebrationHost prevents replays of the plain one; `source` rides along for analytics only.
    // Returned rather than posted here: this runs before the caller knows whether a 'signup'
    // celebration also needs to queue ahead of it (SignupFlow, on a full-profile signup), and
    // posting straight to the intent channel from here can win a race against that later post and
    // land in the wrong queue order. The caller (resolvePostSignupDestination /
    // useCallbackRedirect / useNativePostAuth) posts this intent itself, immediately before
    // `navigate()`, in signup-then-attend order — see SignupFlow.tsx's post site.
    // Fire-and-forget: this path is best-effort, so refresh the affected caches in the background
    // instead of blocking the user's first authed navigation on the refetches settling.
    // Shared sweep (design §4.1). The GOING_EVENT intent payload only carries the event id, but
    // the response names every space the event touches (review F2): every bucket. Only
    // `joined` + `pending` + `pin_required` changed membership (`skipped` is the ordinary
    // already-a-member re-attend, `failed` rolled its write back), so the rosters take the
    // narrower set — `these` when that set is non-empty, `none` when it isn't (an attend that
    // landed entirely in `skipped` / `failed`).
    //
    // That `none` is a stronger claim than it looks, and it holds HERE for a reason that does not
    // travel: `skipped` covers "already a member" (backend `_enroll_one` returns it for any
    // existing non-DECLINED membership), so on a transport that RETRIES, attempt 1's committed
    // join hides behind attempt 2's `skipped` and the empty `joined + pending` is a lie. This path
    // makes a single un-retried request — a direct axios call, not a React Query mutation, and the
    // only re-issue the axios layer performs is the response interceptor's 401 token refresh,
    // which re-sends a request the server rejected before touching a row. `useToggleGoingEvent`
    // runs the same mapping under `authAwareRetry` and therefore has to detect the retry
    // (see its `membershipChangeOf`). If this ever moves onto a retrying transport, that detection
    // has to come with it.
    //
    // `unknown` DOES apply here, on two inputs: a 201 whose body the guard above could not read,
    // and (in the catch below) a rejected POST, which may still have committed.
    // `requestedSpacesOf` is `pending` plus the PIN Spaces (TECH-663 `pin_required`), PENDING rows
    // the backend reports in their own bucket, so they changed membership just as `pending` did.
    const requested = readable ? requestedSpacesOf(data) : [];
    const changedSpaceIds = readable
      ? [...data.joined, ...requested].map(space => space.space_id)
      : [];
    const namedSpaceIds = readable
      ? [...data.joined, ...requested, ...data.skipped, ...data.failed].map(space => space.space_id)
      : [];
    // An unreadable body names no space, so no roster is swept — but the global membership
    // surfaces (my-joined, community list, community detail) all fire, because "I cannot read the
    // response" is not "nothing happened". A readable body whose `pin_required` lists an item
    // `requestedSpacesOf` had to drop is not a complete claim either (a request went to a Space it
    // no longer names): `unknown`, over every Space it does name.
    const membershipChange: SpaceMembershipChange = !readable
      ? { kind: 'unknown', candidateIds: [] }
      : !requestedSpacesAreComplete(data)
        ? { kind: 'unknown', candidateIds: namedSpaceIds }
        : changedSpaceIds.length === 0
          ? { kind: 'none' }
          : { kind: 'these', ids: changedSpaceIds };
    invalidateGoingEventQueries(queryClient, {
      id: intent.eventId,
      spaceIds: namedSpaceIds,
      membershipChange,
    });

    // When the server asks for a ticket (TECH-575), the celebration that comes back is the
    // ticketed one, in place of the plain event_going celebration: it is the same moment, with
    // the question in it. Like the plain one, it marks the first-Going carousel complete on show.
    const registration = readable ? buildRegistrationCelebration(data, 'post_auth') : null;
    const source = intent.source ?? null;
    if (pins.length > 0) {
      // TECH-663: the PIN sheet in place of either celebration. It carries the ticket question as
      // its last step, and like them it marks the first-Going carousel complete on show.
      return {
        ...fallbackTarget,
        intents: [
          {
            surface: 'celebration',
            payload: {
              kind: 'event_pin',
              source,
              eventPin: {
                event_id: intent.eventId,
                spaces: pins,
                registration,
                surface: 'post_auth',
              },
            },
          },
        ],
      };
    }
    return {
      ...fallbackTarget,
      intents: [
        {
          surface: 'celebration',
          payload: registration
            ? { kind: 'registration_check', source, registration }
            : { kind: 'event_going', source },
        },
      ],
    };
  } catch (error) {
    // A Going saved before sign-in can meet an event cancelled since (409 EVENT_CANCELLED): that is
    // the stale page the guest left, not a failure, so it lands on the cancelled page without a
    // toast or an error log (the invalidation below refetches it).
    if (!isEventCancelledError(error)) {
      logger.error(
        '[executePostAuthIntent] Failed to attend event after auth:',
        sanitizeError(error),
      );
    }
    // The POST may have committed (#1058) and the intent carries no space ids, so candidateIds is
    // empty.
    invalidateGoingEventQueries(queryClient, {
      id: intent.eventId,
      spaceIds: [],
      membershipChange: { kind: 'unknown', candidateIds: [] },
    });
    return fallbackTarget;
  }
}

/**
 * How long the replay's toast stays: it is shown before the landing (the callback, then the
 * post's read and the Post sheet, ~2.5 s in the browser check), so a default 4 s toast was over
 * the sheet for about a second.
 */
const REPLAY_TOAST_MS = 7000;

const say = (options: ToastOptions) => toast({ ...options, duration: REPLAY_TOAST_MS });

type VotePollIntentPayload = Extract<AuthIntentPayload, { kind: typeof AuthIntent.VOTE_POLL }>;

/** The poll block of a vote's response, without the join fields. */
const pollOf = ({
  held: _held,
  held_space_id: _heldSpaceId,
  joined_space_id: _joinedSpaceId,
  ...poll
}: PollVoteResponse) => poll;

/**
 * The post's Spaces as the viewer now sees them, for a name in a toast; empty when the read fails.
 * A read of its own (not the landing's): this runs before the app lands on the post.
 */
async function readPollSpaces(
  questionId: string,
  headers: Record<string, string>,
): Promise<SpaceSummary[]> {
  try {
    const { data } = await getQuestionByID(questionId, { headers });
    return data.space_summaries;
  } catch {
    return [];
  }
}

const nameIn = (spaces: SpaceSummary[], id: string | null): string | null =>
  (id ? spaces.find(space => space.id === id)?.name : undefined) ?? null;

/**
 * Casts the answer a guest saved signed out (TECH-197 § Voting "Signed out"; CONTRACT § 6): after
 * sign-in on any device (web callback, signup, native, a magic link opened elsewhere), lands on the
 * poll (`/questions/{questionId}`, the Post sheet) and sends `{ option_ids, via_sign_in: true }`.
 * The membership rule applies as for any answer:
 * - counted → "Your answer is in" (B8, 1c+); joined → B10's toast, held → B11's (and the card's
 *   footer from the returned block); either way the Space's membership caches are refreshed;
 * - 409 `POLL_CLOSED` → "This poll ended before you signed in";
 * - 404 (no longer readable) → "Only members of {Space} can answer" (the Space the post page's
 *   refusal names) or "Only people Going to {Event} can answer"; nothing named, nothing said (the
 *   post page shows its own refusal);
 * - 422 `POLL_OPTION_UNKNOWN` (an edit replaced the options) → "This poll changed. Have another
 *   look.";
 * - 403 `POLL_CANNOT_JOIN` → "Only members of {Space} can answer";
 * - 403 `POLL_PIN_REQUIRED` → the poll's card opens the PIN drawer (E16) still holding the pick (a
 *   `poll-pin` intent);
 * nothing cast in each. An expired intent never gets here (`readAuthIntent`): it lands, nothing cast.
 * Best effort, as GOING_EVENT: any other failure says E10's line and still lands.
 */
async function executeVotePoll({
  intent,
  redirectPath,
  queryClient,
  accessToken,
}: {
  intent: VotePollIntentPayload;
  redirectPath: string | null;
  queryClient: QueryClient;
  accessToken?: string | null;
}): Promise<PostAuthIntentExecutionResult> {
  const target = resolveAuthIntent(intent, redirectPath);
  if (!accessToken) {
    logger.error('[executePostAuthIntent] Missing access token for the poll answer');
    return target;
  }
  const headers = { Authorization: `Bearer ${accessToken}` };
  const { questionId, optionIds } = intent;
  try {
    const { data } = await votePoll(
      questionId,
      { option_ids: optionIds, via_sign_in: true },
      { headers },
    );
    writePollIntoCaches(queryClient, questionId, pollOf(data));
    const changedSpaceId = data.joined_space_id ?? (data.held ? data.held_space_id : null);
    const spaces = changedSpaceId ? await readPollSpaces(questionId, headers) : [];
    if (data.joined_space_id) {
      const name = nameIn(spaces, data.joined_space_id);
      say({
        title: name ? POLL_COPY.answerInJoined(name) : POLL_COPY.answerIn,
        variant: 'success',
      });
    } else if (data.held) {
      const name = nameIn(spaces, data.held_space_id);
      say(
        name
          ? { title: POLL_COPY.joinRequestSent(name), variant: 'info' }
          : { ...REQUEST_SENT_TOAST, variant: 'info' },
      );
    } else {
      say({ title: POLL_COPY.answerIn, variant: 'success' });
    }
    if (changedSpaceId) {
      invalidateMembershipQueries(queryClient, { spaceId: changedSpaceId, scope: 'self' });
    }
    return target;
  } catch (error) {
    const code = pollErrorCode(error);
    if (code === POLL_ERROR_CODES.pinRequired) {
      return {
        ...target,
        intents: [
          {
            surface: 'poll-pin',
            payload: { questionId, optionIds, spaceId: pollErrorSpaceId(error) },
          },
        ],
      };
    }
    if (code === POLL_ERROR_CODES.closed) {
      say({ title: POLL_COPY.pollEndedBeforeSignIn, variant: 'info' });
      return target;
    }
    if (code === POLL_ERROR_CODES.optionUnknown) {
      say({ title: POLL_COPY.pollChanged, variant: 'info' });
      return target;
    }
    if (code === POLL_ERROR_CODES.cannotJoin) {
      const spaceId = pollErrorSpaceId(error);
      const name = spaceId ? nameIn(await readPollSpaces(questionId, headers), spaceId) : null;
      say({
        title: name ? POLL_COPY.onlyMembersCanAnswer(name) : POLL_COPY.onlyMembersCanAnswerUnnamed,
        variant: 'info',
      });
      return target;
    }
    if (getErrorStatus(error) === 404) {
      const refusal = await readRefusal(questionId, headers);
      if (refusal) say({ title: refusal, variant: 'info' });
      return target;
    }
    logger.error('[executePostAuthIntent] Failed to cast the poll answer:', sanitizeError(error));
    say({ title: POLL_COPY.answerFailed, variant: 'error' });
    return target;
  }
}

/**
 * Why a poll the guest could read is not readable now, in E6's words: the post page's own refusal
 * names the Space (or the Event) when the viewer may be told it. Null when it names nothing.
 */
async function readRefusal(
  questionId: string,
  headers: Record<string, string>,
): Promise<string | null> {
  try {
    await getQuestionByID(questionId, { headers });
    return null;
  } catch (error) {
    const refusal = parseQuestionAccessError(error);
    if (
      refusal?.type === QuestionAccessErrorCode.SPACE_MEMBERSHIP_REQUIRED &&
      refusal.space?.name
    ) {
      return POLL_COPY.onlyMembersCanAnswer(refusal.space.name);
    }
    if (refusal?.type === QuestionAccessErrorCode.EVENT_CHECKIN_REQUIRED && refusal.event?.name) {
      return POLL_COPY.onlyPeopleGoingCanAnswer(refusal.event.name);
    }
    return null;
  }
}

type LivePollAnswersPayload = Extract<
  AuthIntentPayload,
  { kind: typeof AuthIntent.LIVE_POLL_ANSWERS }
>;

/**
 * A busy `/claim` (429 `LIVE_RATE_LIMITED`, `details.reason` "busy", CONTRACT 11:25 note) is sent again after its
 * full Retry-After, at most this many times more, and only when the wait is this short. Anything longer, or a 429
 * that is not busy (the rate limit), gives up: the answers stay the guest's and the same browser still attaches.
 */
const CLAIM_BUSY_RETRIES = 2;
const CLAIM_BUSY_MAX_WAIT_MS = 10_000;

/** The wait before sending a busy claim again, or null when it must not be sent again. */
const claimBusyWaitMs = (error: unknown): number | null => {
  if (getErrorStatus(error) !== 429 || pollErrorCode(error) !== LIVE_ERROR_CODES.rateLimited)
    return null;
  if (getErrorDetails(error)?.reason !== 'busy') return null;
  const headers = (error as { response?: { headers?: Record<string, unknown> } } | null)?.response
    ?.headers;
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
  const seconds = typeof raw === 'string' || typeof raw === 'number' ? Number(raw) : NaN;
  const ms = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 1_000;
  return ms <= CLAIM_BUSY_MAX_WAIT_MS ? ms : null;
};

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/** `/claim`, sent again after Retry-After while the server says it is busy (429, CONTRACT 11:25 note). */
async function claimWhenFree(
  code: string,
  guestClaim: string,
  headers: Record<string, string>,
): Promise<LiveClaimRead> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return (await claimLive(code, { guest_claim: guestClaim }, { headers })).data;
    } catch (error) {
      const waitMs = claimBusyWaitMs(error);
      if (waitMs === null || attempt >= CLAIM_BUSY_RETRIES) throw error;
      await wait(waitMs);
    }
  }
}

/** The join Space's name for a toast, from the room's public read; null when it cannot be read. */
async function liveJoinSpaceName(code: string): Promise<string | null> {
  try {
    const read = await getLiveState(code);
    return read.ok ? (read.data.join_space?.name ?? null) : null;
  } catch {
    return null;
  }
}

/**
 * A live poll's guest answers after sign-in, on whichever device finished it (TECH-867 § Answering "Attach to an
 * account, on any device"; CONTRACT § 4.13, § 6, U2, 11:25 note): lands on `/live/{liveCode}` (the sheet, still
 * waiting or on the open question) after `POST /live/{code}/claim { guest_claim }`, which moves the guest's
 * answers to the account (none counted twice) and runs the live join on answer; then the join by type, as the
 * person asked from the join card:
 * - joined by the claim (an open Space and answers attached): "You've joined {Space}.";
 * - open, nothing joined yet: `/join {}`, the same toast;
 * - approval: `/join { request_access: true }`, today's request feedback;
 * - PIN: the live sheet opens today's PIN drawer (a `live-pin` intent);
 * - member, pending, none: nothing more.
 * 403 `LIVE_CLAIM_FORBIDDEN` (the account is not the email this sign-in went to): nothing attached or joined, no
 * toast (it blames no one); the answers stay counted as the guest's, and the same browser's token still attaches
 * them on landing. Best effort, as the other replays: any other failure lands with nothing said.
 */
async function executeLivePollAnswers({
  intent,
  redirectPath,
  queryClient,
  accessToken,
}: {
  intent: LivePollAnswersPayload;
  redirectPath: string | null;
  queryClient: QueryClient;
  accessToken?: string | null;
}): Promise<PostAuthIntentExecutionResult> {
  const target = resolveAuthIntent(intent, redirectPath);
  if (!accessToken) {
    logger.error('[executePostAuthIntent] Missing access token for the live poll claim');
    return target;
  }
  const headers = { Authorization: `Bearer ${accessToken}` };
  const code = intent.liveCode;
  let claimed: LiveClaimRead;
  try {
    claimed = await claimWhenFree(code, intent.guestClaim, headers);
  } catch (error) {
    if (pollErrorCode(error) === LIVE_ERROR_CODES.claimForbidden) {
      logger.info(
        '[executePostAuthIntent] Live poll claim belongs to another sign-in; nothing attached',
      );
    } else if (getErrorStatus(error) === 429) {
      logger.info(
        '[executePostAuthIntent] Live poll claim refused as busy or rate limited; left to the guest token',
      );
    } else {
      logger.error(
        '[executePostAuthIntent] Failed to claim the live poll answers:',
        sanitizeError(error),
      );
    }
    return target;
  }
  queryClient.setQueryData(liveMeKey(code), claimed.me);
  // The guest's answers are the account's now: this device keeps no token for them (FE-L3b review). Only when
  // answers moved: `/claim` also answers 200 with nothing attached for a dead claim (a spent one, or Redis not
  // answering), and the token is then the same browser's only way back to them; `/open` and `/me` still use it.
  if (claimed.attached_questions > 0) forgetGuestToken(code);

  const joinedToast = async () => {
    const name = await liveJoinSpaceName(code);
    if (name) say({ title: LIVE_POLL_COPY.joinedSpace(name), variant: 'success' });
    invalidateMembershipQueries(queryClient, { scope: 'self' });
  };

  if (claimed.joined_space_id) {
    await joinedToast();
    return target;
  }
  const kind = claimed.me.join_type_for_me;
  if (kind === 'pin') {
    return { ...target, intents: [{ surface: 'live-pin', payload: { liveCode: code } }] };
  }
  if (kind !== 'open' && kind !== 'approval') return target;

  const body: LiveJoinBody = kind === 'approval' ? { request_access: true } : {};
  try {
    const { data } = await joinLive(code, body, { headers });
    queryClient.setQueryData(liveMeKey(code), data.me);
    if (data.status === 'approved') {
      await joinedToast();
    } else {
      say({ ...REQUEST_SENT_TOAST, variant: 'info' });
      invalidateMembershipQueries(queryClient, { scope: 'self' });
    }
  } catch (error) {
    if (pollErrorCode(error) === POLL_ERROR_CODES.pinRequired) {
      return { ...target, intents: [{ surface: 'live-pin', payload: { liveCode: code } }] };
    }
    logger.error(
      '[executePostAuthIntent] Failed to join from the live poll:',
      sanitizeError(error),
    );
  }
  return target;
}
